import React, { useState, useRef } from 'react';
import GenericModal from '../genericModal/GenericModal';
import { notifyError, notifySuccess } from '../Notification';
import { faTrash, faCloudUploadAlt, faImage, faTimesCircle, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from '../Button/Button';
import './AdUploadModal.css';

const AdUploadModal = ({ show, onClose, deckId, onUpload, existingAds, onDeleteAd }) => {
  const [adImages, setAdImages] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Image dimension requirements with tolerance
  const TARGET_WIDTH = 1280;
  const TARGET_HEIGHT = 120;
  const TOLERANCE = 5; // ±5 pixels tolerance

  const validateFile = (file) => {
    return new Promise((resolve, reject) => {
      if (file.size > 200 * 1024) {
        reject(`File ${file.name} is too large. Maximum size is 200KB.`);
        return;
      }

      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          const widthInRange = img.width >= TARGET_WIDTH - TOLERANCE && img.width <= TARGET_WIDTH + TOLERANCE;
          const heightInRange = img.height >= TARGET_HEIGHT - TOLERANCE && img.height <= TARGET_HEIGHT + TOLERANCE;
          
          if (!widthInRange || !heightInRange) {
            reject(
              `Image ${file.name} has incorrect dimensions (${img.width}x${img.height}). ` +
              `Required: ${TARGET_WIDTH}x${TARGET_HEIGHT} pixels (±${TOLERANCE}px tolerance).`
            );
          } else {
            resolve({ file, preview: reader.result });
          }
        };
        img.onerror = () => reject(`Failed to load image ${file.name}`);
        img.src = event.target.result;
      };
      reader.onerror = () => reject(`Failed to read file ${file.name}`);
      reader.readAsDataURL(file);
    });
  };

  const processFiles = async (files) => {
    const fileArray = Array.from(files);
    const totalImages = fileArray.length + adImages.length + existingAds.length;

    if (totalImages > 4) {
      notifyError(`You can upload a maximum of 4 images. You currently have ${existingAds.length + adImages.length} images.`);
      return;
    }

    setIsProcessing(true);
    const validatedFiles = [];

    for (const file of fileArray) {
      try {
        const result = await validateFile(file);
        validatedFiles.push(result);
      } catch (error) {
        notifyError(error);
      }
    }

    if (validatedFiles.length > 0) {
      setAdImages(prev => [...prev, ...validatedFiles.map(v => v.file)]);
      setPreviews(prev => [...prev, ...validatedFiles.map(v => v.preview)]);
      notifySuccess(`${validatedFiles.length} image(s) added successfully.`);
    }

    setIsProcessing(false);
  };

  const handleFileChange = (e) => {
    processFiles(e.target.files);
    e.target.value = ''; // Reset input
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      processFiles(files);
    }
  };

  const handleRemoveImage = (index) => {
    setAdImages(prev => prev.filter((_, i) => i !== index));
    setPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (adImages.length === 0) {
      notifyError('Please select at least one image to upload.');
      return;
    }
    
    setIsUploading(true);
    try {
      await onUpload(adImages);
      // Success! Clear local state
      setAdImages([]);
      setPreviews([]);
      // Note: Parent component should refetch ads data after onUpload resolves
    } catch (error) {
      notifyError('Failed to upload images. Please try again.');
      console.error('Upload error:', error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteExisting = (adId) => {
    if (window.confirm('Are you sure you want to delete this ad image?')) {
      onDeleteAd(adId);
    }
  };

  const totalImages = existingAds.length + adImages.length;
  const remainingSlots = 4 - totalImages;

  return (
    <GenericModal
      size='lg'
      show={show}
      onClose={onClose}
      header={
        <div className="custom-modal-header">
          <div className="modern-preview-icon">
            <svg width="28" height="28" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <div className="deck-modal-header-content">
            <h1 className='slide1-main-title'>Upload Ad Images</h1>
          </div>
        </div>
      }
      body={
        <div className="ad-upload-container">
          {/* Upload Zone */}
          <div
            className={`file-input-container ${isDragging ? 'dragging' : ''} ${isProcessing || isUploading ? 'processing' : ''}`}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={() => !isProcessing && !isUploading && fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*"
              onChange={handleFileChange}
              className="file-input"
              disabled={isProcessing || isUploading || remainingSlots === 0}
            />
            <div className="upload-content">
              <FontAwesomeIcon icon={faCloudUploadAlt} className="upload-icon" />
              <p className="upload-text">
                {isProcessing ? (
                  'Processing images...'
                ) : isUploading ? (
                  'Uploading to server...'
                ) : isDragging ? (
                  'Drop images here'
                ) : (
                  <>
                    <strong>Click to upload</strong> or drag and drop
                  </>
                )}
              </p>
              <p className="upload-specs">
                {remainingSlots > 0 ? (
                  <>1280x120 pixels (±5px) • Max 200KB • {remainingSlots} slot{remainingSlots !== 1 ? 's' : ''} remaining</>
                ) : (
                  <span className="slots-full">Maximum 4 images reached</span>
                )}
              </p>
            </div>
          </div>

          {/* Image Counter / Upload Status */}
          {totalImages > 0 && (
            <div className={`image-counter ${isUploading ? 'uploading' : ''}`}>
              <FontAwesomeIcon icon={isUploading ? faSpinner : faImage} className={isUploading ? 'fa-spin' : ''} />
              <span>
                {isUploading 
                  ? `Uploading ${adImages.length} image${adImages.length !== 1 ? 's' : ''}...`
                  : `${totalImages} of 4 images uploaded`
                }
              </span>
            </div>
          )}

          {/* Previews Grid */}
          {totalImages > 0 && (
            <div className="previews-container">
              {/* Uploading Overlay */}
              {isUploading && (
                <div className="uploading-overlay">
                  <div className="uploading-content">
                    <FontAwesomeIcon icon={faSpinner} className="fa-spin uploading-spinner" />
                    <p className="uploading-text">Uploading your images...</p>
                    <p className="uploading-subtext">Please don't close this window</p>
                  </div>
                </div>
              )}

              {/* Existing Ads */}
              {existingAds.map((ad) => (
                <div key={ad.ad_id} className="preview-item existing">
                  <div className="preview-image-wrapper">
                    <img src={ad.image_url} alt={`Ad ${ad.ad_id}`} />
                  </div>
                  <div className="preview-item-actions">
                    <span className="existing-badge">Current</span>
                    <button
                      className="delete-button"
                      onClick={() => handleDeleteExisting(ad.ad_id)}
                      title="Delete image"
                      aria-label="Delete image"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                      Delete
                    </button>
                  </div>
                </div>
              ))}

              {/* New Uploads */}
              {previews.map((preview, index) => (
                <div key={`new-${index}`} className="preview-item new">
                  <div className="preview-image-wrapper">
                    <img src={preview} alt={`Preview ${index + 1}`} />
                  </div>
                  <div className="preview-item-actions">
                    <span className="new-badge">New</span>
                    <button
                      className="delete-button"
                      onClick={() => handleRemoveImage(index)}
                      title="Remove image"
                      aria-label="Remove image"
                    >
                      <FontAwesomeIcon icon={faTimesCircle} />
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Empty State */}
          {totalImages === 0 && (
            <div className="empty-state">
              <FontAwesomeIcon icon={faImage} className="empty-icon" />
              <p>No images uploaded yet</p>
            </div>
          )}
        </div>
      }
      footer={
        <div className='ad-upload-modal-footer'>
          <Button variant="secondary" onClick={onClose} disabled={isUploading}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleUpload}
            disabled={adImages.length === 0 || isProcessing || isUploading}
          >
            {isUploading ? (
              <>
                <FontAwesomeIcon icon={faSpinner} className="fa-spin" style={{ marginRight: '0.5rem' }} />
                Uploading...
              </>
            ) : isProcessing ? (
              'Processing...'
            ) : (
              `Upload${adImages.length > 0 ? ` (${adImages.length})` : ''}`
            )}
          </Button>
        </div>
      }
    />
  );
};

export default AdUploadModal;